import React, { useEffect, useState } from 'react';
import { sanitizeInputValue } from 'utils';

const checkType = (value, dataType, name, canBeNull = false) => {
  if (dataType.some && dataType.some((type) => (typeof value) === type)) return;
  if (typeof value === dataType) return;
  if (canBeNull && value === null) return;
  throw (`${name} received displayValue of ${value}: ${typeof value}, expected: ${dataType}`);
};

const labelAtom = (dataType, Template) => ({ name, displayValue, displayState }) => {
  checkType(displayValue, dataType, name);
  const htmlProps = {};
  if (displayState?.viewBox) htmlProps.viewBox = displayState.viewBox;
  return <Template {...htmlProps} displayState={displayState}>{displayValue}</Template>;
};

const toggleAtom = (Template) => ({ name, eventManager, eventName, bindings, displayState }) => {
  checkType(bindings.value, 'boolean', name);
  return (
    <Template
      displayState={displayState}
      value={bindings.value}
      onClick={eventManager.bind(null, eventName, { ...bindings, value: !bindings.value })}
    />
  );
};

const textareaAtom = (dataType, Template) => ({ name, eventManager, eventName, bindings, displayState }) => {
  checkType(bindings.value, dataType, name);
  return (
    <Template
      displayState={displayState}
      placeholder={bindings.placeholder}
      value={bindings.value}
      rows={bindings.rows}
      onChange={({ target: { value } }) => eventManager(eventName, { ...bindings, value })}
    />
  );
};

const textareaGrowAtom = (dataType, Template) => ({ name, eventManager, eventName, bindings, displayState }) => {
  checkType(bindings.value, dataType, name);
  let inputRef = React.createRef();

  if (inputRef?.current) {
    let newHeight = Math.min(inputRef.current.scrollHeight, window?.innerWidth < 768 ? 120 : 168) + "px";
    inputRef.current.style.height = "1px";
    inputRef.current.style.height = newHeight;
  }

  useEffect(() => {
    textAreaAdjust();
  }, []);

  function textAreaAdjust() {
    if (inputRef?.current) {
      let newHeight = Math.min(inputRef.current.scrollHeight, window?.innerWidth < 768 ? 120 : 168) + "px";
      inputRef.current.style.height = "1px";
      inputRef.current.style.height = newHeight;
    }
  }

  return (
    <Template
      ref={inputRef}
      displayState={displayState}
      placeholder={bindings.placeholder}
      value={bindings.value}
      onKeyUp={textAreaAdjust}
      onChange={({ target: { value } }) => eventManager(eventName, { ...bindings, value })}
    />
  );
};

const currencyAtom = (dataType, Template, keyDown) => ({ name, eventManager, eventName, bindings, displayState }) => {
  checkType(bindings.value, dataType, name, true);
  return (
    <Template
      displayState={displayState}
      placeholder={bindings.placeholder}
      onFocus={(e) => e.target.setSelectionRange(1, 100)}
      onKeyDown={(e) => {
        e.persist();
        const start = e.target.value ? e.target.selectionStart : 1;
        setTimeout(() => e.target.setSelectionRange(start + 1, 100), 20);
      }}
      value={bindings.value === null ? '' : `$${(bindings.value / 100).toFixed(2)}`}
      onChange={({ target: { value } }) => {
        if (!value) return value;
        const valueInCents = parseFloat(value.replace(/[^\d.]/, '')) * 100;
        eventManager(eventName, { ...bindings, value: valueInCents });
      }}
    />
  );
};

const inputAtom = (dataType, Template, keyDown) => ({ name, eventManager, eventName, bindings, displayState }) => {
  checkType(bindings.value, dataType, name, true);
  if (keyDown) {
    return (
      <Template
        displayState={displayState}
        placeholder={bindings.placeholder}
        value={bindings.value}
        onKeyDown={({ target: { value }, key }) => eventManager(`${eventName}(Keydown)`, { ...bindings, key })}
        onChange={({ target: { value } }) => eventManager(eventName, { ...bindings, value: sanitizeInputValue(value) })}
      />
    );
  }
  return (
    <Template
      displayState={displayState}
      placeholder={bindings.placeholder}
      value={bindings.value}
      onChange={({ target: { value } }) => eventManager(eventName, { ...bindings, value: sanitizeInputValue(value) })}
    />
  );
};

const notClickableInputAtom = (dataType, Template, keyDown) => ({ name, eventManager, eventName, bindings, displayState }) => {
  checkType(bindings.value, dataType, name, true);
  if (keyDown) {
    return (
      <Template
        displayState={displayState}
        placeholder={bindings.placeholder}
        value={bindings.value}
        onKeyDown={({ target: { value }, key }) => eventManager(`${eventName}(Keydown)`, { ...bindings, key })}
        onChange={({ target: { value } }) => eventManager(eventName, { ...bindings, value: sanitizeInputValue(value) })}
      />
    );
  }
  return (
    <Template
      displayState={displayState}
      placeholder={bindings.placeholder}
      value={bindings.value}
      onClick={(event) => { event.stopPropagation() }}
      onChange={({ target: { value } }) => eventManager(eventName, { ...bindings, value })}
    />
  );
};

const timeInputAtom = (dataType, Template) => ({ name, eventManager, eventName, bindings, displayState }) => {
  checkType(bindings.value, dataType, name, true);
  return (
    <Template
      displayState={displayState}
      placeholder={bindings.placeholder}
      value={bindings.value}
      type="time"
      pattern="[0-9]{2}:[0-9]{2}"
      onChange={({ target: { value } }) => eventManager(eventName, { ...bindings, value })}
    />
  );
};

const buttonAtom = (dataType, Template) => ({ name, eventManager, eventName, bindings, displayValue, displayState }) => {
  // checkType(stateData.displayValue, dataType, name);
  const clickHandler = (e) => {
    e.stopPropagation();
    eventManager(eventName, bindings);
  };

  const disabled = bindings?.hasOwnProperty('isDisabled') && bindings.isDisabled;
  // checkType(stateData.displayValue, dataType, name);
  const htmlProps = {};
  if (displayState?.viewBox) htmlProps.viewBox = displayState.viewBox;
  return (
    <Template {...htmlProps} disabled={disabled} displayState={displayState} onClick={clickHandler}>
      {displayValue}
    </Template>
  );
};

const linkAtom = (Template) => ({ name, eventManager, eventName, bindings, displayValue, displayState }) => {
  checkType(displayValue, 'string', name);
  checkType(bindings.href, 'string', name);
  return (
    <Template href={bindings.href} displayState={displayState} target={bindings.target || '_blank'} onClick={event => event.stopPropagation()}>
      {displayValue}
    </Template>
  );
};

const imageAtom = (Template) => ({ name, displayValue, displayState }) => {
  checkType(displayValue, ['string', 'object'], name);
  return <Template src={displayValue} displayState={displayState} />;
};

const videoAtom = (Template) => ({ name, displayValue, displayState }) => {
  checkType(displayValue, ['string', 'object'], name);
  return <Template src={displayValue} displayState={displayState} controls />;
};

const uploadAtom = (dataType, Template) => ({ name, bindings, displayValue, displayState, eventManager, eventName }) => (
  <Template displayState={displayState}>
    <input
      onChange={({ target: { files } }) => {
        eventManager(eventName, { ...bindings, value: files[0] });
        window.ReactNativeWebView?.postMessage?.('cameraSelected');
      }}
      type="file"
      capture="camera"
      style={{ display: 'none' }}
      accept={dataType}
    />
    {displayValue}
  </Template>
);

const dropUploadAtom = (dataType, Template) => ({ name, bindings, displayValue, displayState, eventManager, eventName }) => {
  const [isHovered, setIsHovered] = useState(false);

  const onDrop = (event) => {
    stopEvent(event);
    const { files } = event.dataTransfer;
    eventManager(eventName, { ...bindings, value: files })
    setIsHovered(false);
  }

  const onDragOver = (event) => {
    stopEvent(event);
    setIsHovered(true);
  }

  const onDragLeave = (event) => {
    stopEvent(event);
    setIsHovered(false);
  }

  const stopEvent = event => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <Template
      displayState={displayState}
      isHovered={isHovered}
      onDrop={onDrop}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
    >
      <input
        onChange={({ target: { files } }) => {
          eventManager(eventName, { ...bindings, value: files });
          window.ReactNativeWebView?.postMessage?.('cameraSelected');
        }}
        capture="camera"
        type="file"
        style={{ display: 'none' }}
        accept={dataType}
        multiple="multiple"
      />
      {displayValue}
    </Template>
  )
};

const multipleUploadAtom = (dataType, Template) => ({ name, bindings, displayValue, displayState, eventManager, eventName }) => (
  <Template displayState={displayState}>
    <input
      onChange={({ target: { files } }) => {
        eventManager(eventName, { ...bindings, value: files });
        window.ReactNativeWebView?.postMessage?.('cameraSelected');
      }}
      type="file"
      style={{ display: 'none' }}
      accept={dataType}
      multiple={bindings?.singleFileUpload ? null : 'multiple'}
    />
    {displayValue}
  </Template>
);

const customAtom = (Template) => ({ name, bindings, displayValue, eventManager, eventName }) => {
  // checkType(displayValue, ['string', 'object'], name);
  if (displayValue._onChange) {
    const handler = {
      handle(value) {
        eventManager(eventName, { ...bindings, value });
      }
    };
    displayValue[displayValue.onChangeField] = displayValue._onChange.bind(null, handler);
  }
  return <Template {...displayValue} />;
};

const AtomComponent = ({ type, dataType, template }) => {
  switch (type) {
    case 'button':
      return buttonAtom(dataType, template);
    case 'input':
      return inputAtom(dataType, template);
    case 'currency':
      return currencyAtom(dataType, template);
    case 'textarea':
      return textareaAtom(dataType, template);
    case 'textareaGrow':
      return textareaGrowAtom(dataType, template);
    case 'keyInput':
      return inputAtom(dataType, template, true);
    case 'timeInput':
      return timeInputAtom(dataType, template)
    case 'label':
      return labelAtom(dataType, template);
    case 'toggle':
      return toggleAtom(template);
    case 'image':
      return imageAtom(template);
    case 'video':
      return videoAtom(template);
    case 'upload':
      return uploadAtom(dataType, template);
    case 'dropUpload':
      return dropUploadAtom(dataType, template);
    case 'uploadMultiple':
      return multipleUploadAtom(dataType, template);
    case 'link':
      return linkAtom(template);
    case 'custom':
      return customAtom(template);
    case 'notClickableInput':
      return notClickableInputAtom(dataType, template)
    default:
      throw (`Invalid atom type: ${type}`);
  }
};

const Atom = (params) => {
  const comp = AtomComponent(params);
  // eslint-disable-next-line no-underscore-dangle
  comp._appReactType = params.type;
  return comp;
};

export default Atom;
